import { Drawer, Input, Select, message } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import { createPfEmployee, fetchAllPfEmployees, updatePfEmployee } from "../../../core/data/redux/payroll/Accounting/providentFund/pfEmployee/pfEmployeeActions";
import { status } from "../../../core/common/selectoption/selectoption";
import { fetchAllPfPolicies } from "../../../core/data/redux/payroll/Accounting/providentFund/pfpolicies/pfPolicyActions";
import { getEmployees } from "../../../core/data/redux/employee/employeeAction";
import { filterByStatus } from "../../../core/utils/filterActiveData";

const inputFields = [
    { label: "UAN", key: "UAN" },
    { label: "Account Number", key: "pfAccountNumber" },
    { label: "Nominee Name", key: "nomineeName" },
];
// ⬇️ Popular nominee relations
const NOMINEE_RELATIONS = [
    "Spouse",
    "Father",
    "Mother",
    "Son",
    "Daughter",
    "Brother",
    "Sister",
    "Father-in-law",
    "Mother-in-law",
    "Other",
];
const PfEmployeeModal: React.FC<any> = ({
    modalType,
    handleCancel,
    isModalVisible,
    selectedRowData,
    setTableKey
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useSelector((state: any) => state.auth);
    const { loadingStates } = useSelector((state: any) => state.providentFund.pfEmployeeMapping);
    const { records: PfPolicy } = useSelector((state: any) => state.providentFund.pfPolicy);
    const { employees } = useSelector((state: any) => state.employee);
    const pfEnabledEmployees = useMemo(() => { return employees.filter((emp: any) => emp.pfEnable === 1); }, [employees]);
    const activePfPolicy = filterByStatus(PfPolicy, 'active', 'status');

    const activepfEnableEmpl = filterByStatus(pfEnabledEmployees, "active", "employee_status");
    const isLoading = modalType === "edit" ? loadingStates.update : loadingStates.create;

    const [formData, setFormData] = useState<any>({
        EmployeeID: undefined,
        pfPolicyId: undefined,
        UAN: "",
        pfAccountNumber: "",
        nomineeName: "",
        nomineeRelation: "",
        status: "active",
        createdBy: user?.UserId || 1,
    });

    const [errors, setErrors] = useState<any>({});

    useEffect(() => {
        if (!PfPolicy.length) {
            dispatch(fetchAllPfPolicies());
        }
        if (!employees.length) {
            dispatch(getEmployees());
        }
    }, [dispatch, PfPolicy.length, employees.length]);

    const resetForm = useCallback(() => {
        setFormData({
            EmployeeID: undefined,
            pfPolicyId: undefined,
            UAN: "",
            pfAccountNumber: "",
            nomineeName: "",
            nomineeRelation: "",
            status: "active",
            createdBy: user?.UserId || 1,
        });
        setErrors({});
    }, [user?.UserId]);

    useEffect(() => {
        if (modalType === "edit" && selectedRowData) {
            setFormData({
                EmployeeID: selectedRowData.EmployeeID || undefined,
                pfPolicyId: selectedRowData.pfPolicyId || undefined,
                UAN: selectedRowData.UAN || "",
                pfAccountNumber: selectedRowData.pfAccountNumber || "",
                nomineeName: selectedRowData.nomineeName || "",
                nomineeRelation: selectedRowData.nomineeRelation || "",
                status: selectedRowData.status || "active",
                createdBy: selectedRowData.createdBy || user?.UserId || 1,
            });
            setErrors({});
        } else {
            resetForm();
        }
    }, [modalType, selectedRowData, resetForm, user?.UserId]);

    const handleChange = (field: string, value: any) => {
        setFormData((prev: any) => ({ ...prev, [field]: value }));
        setErrors((prev: any) => ({ ...prev, [field]: "" }));
    };

    const validateForm = () => {
        let valid = true;
        const newErrors: any = {};

        if (!formData.EmployeeID) {
            newErrors.EmployeeID = "Employee is required.";
            valid = false;
        }

        if (!formData.pfPolicyId) {
            newErrors.pfPolicyId = "Policy is required.";
            valid = false;
        }

        if (!formData.UAN.trim()) {
            newErrors.UAN = "UAN is required.";
            valid = false;
        }

        if (!formData.pfAccountNumber.trim()) {
            newErrors.pfAccountNumber = "Account Number is required.";
            valid = false;
        }

        if (!formData.nomineeName.trim()) {
            newErrors.nomineeName = "Nominee Name is required.";
            valid = false;
        }

        if (!formData.nomineeRelation.trim()) {
            newErrors.nomineeRelation = "Nominee Relation is required.";
            valid = false;
        }
        if (!formData.status.trim()) {
            newErrors.status = "Status is required.";
            valid = false;
        }

        setErrors(newErrors);
        return valid;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            if (modalType === "edit") {
                await dispatch(updatePfEmployee({ id: selectedRowData.pfEmployeeId, data: formData })).unwrap();
                message.success("Employee mapping updated successfully");
            } else {
                await dispatch(createPfEmployee(formData)).unwrap();
                message.success("Employee mapping created successfully");
            }
            await dispatch(fetchAllPfEmployees())
            if (setTableKey) {
                setTableKey((prevKey: any) => prevKey + 1);

            }
            handleCancel();
            resetForm();
        } catch (error:any) {
            message.error(error?.error);
        }
    };
    const nomineeRelationOptions = useMemo(() => {
        const opts = [...NOMINEE_RELATIONS];
        const current = String(formData.nomineeRelation || "").trim();
        if (current && !opts.includes(current)) {
            return [current, ...opts];
        }
        return opts;
    }, [formData.nomineeRelation]);

    return (
        <Drawer
            title={modalType === "edit" ? "Edit Employee Mapping" : "Add Employee Mapping"}
            placement="right"
            width={500}
            onClose={() => {
                handleCancel();
                resetForm();
            }}
            visible={isModalVisible}
        >
            <form>
                {/* Employee Name */}
                <div className="mb-3">
                    <label className="form-label">Employee  <span className="text-danger">*</span></label>
                    <Select
                        className="w-100"
                        value={formData.EmployeeID}
                        onChange={(value) => handleChange("EmployeeID", value)}
                    >
                        {[...activepfEnableEmpl]
                            .sort((a: any, b: any) => a.Name.localeCompare(b.Name))
                            .map((employee: any) => (
                                <Select.Option key={employee.EmployeeID} value={employee.EmployeeID}>
                                    {employee.Name}
                                </Select.Option>
                            ))}
                    </Select>
                    {errors.EmployeeID && <div className="text-danger mt-1">{errors.EmployeeID}</div>}
                </div>

                {/* Policy Name */}
                <div className="mb-3">
                    <label className="form-label">Policy  <span className="text-danger">*</span></label>
                    <Select
                        className="w-100"
                        value={formData.pfPolicyId}
                        onChange={(value) => handleChange("pfPolicyId", value)}
                    >
                        {activePfPolicy.map((policy: any) => (
                            <Select.Option key={policy.pfPolicyId} value={policy.pfPolicyId}>
                                {policy.policyName}
                            </Select.Option>
                        ))}
                    </Select>
                    {errors.pfPolicyId && <div className="text-danger mt-1">{errors.pfPolicyId}</div>}
                </div>

                {/* Other fields remain unchanged */}
                {inputFields.map(({ label, key }) => (
                    <div className="mb-3" key={key}>
                        <label className="form-label">{label}  <span className="text-danger">*</span></label>
                        <Input
                            value={formData[key]}
                            onChange={(e) => handleChange(key, e.target.value)}
                        />
                        {errors[key] && <div className="text-danger mt-1">{errors[key]}</div>}
                    </div>
                ))}
                <div className="mb-3">
                    <label className="form-label">Nominee Relation <span className="text-danger">*</span></label>
                    <Select
                        className="w-100"
                        value={formData.nomineeRelation || undefined}
                        onChange={(value) => handleChange("nomineeRelation", value)}
                        placeholder="Select Nominee Relation"
                        showSearch
                        optionFilterProp="children"
                    >
                        {nomineeRelationOptions.map((rel) => (
                            <Select.Option key={rel} value={rel}>
                                {rel}
                            </Select.Option>
                        ))}
                    </Select>
                    {errors.nomineeRelation && <div className="text-danger mt-1">{errors.nomineeRelation}</div>}
                </div>

                {/* Status */}
                <div className="mb-3">
                    <label className="form-label">Status <span className="text-danger">*</span></label>
                    <Select
                        className="w-100"
                        value={formData.status}
                        onChange={(value) => handleChange("status", value)}
                    >
                        {status.map((option) => (
                            <Select.Option key={option.value} value={option.value}>
                                {option.label}
                            </Select.Option>
                        ))}
                    </Select>
                    {errors.status && <div className="text-danger mt-1">{errors.status}</div>}
                </div>

                {/* Footer buttons */}
                <div className="modal-footer mt-4">
                    <button
                        type="button"
                        className="btn btn-secondary me-2"
                        onClick={() => {
                            handleCancel();
                            resetForm();
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSubmit}
                        disabled={isLoading}
                    >
                        {isLoading && <span className="spinner-border spinner-border-sm me-2" />}
                        {modalType === "edit" ? "Update Employee" : "Add Employee"}
                    </button>
                </div>
            </form>
        </Drawer>
    );
};

export default PfEmployeeModal;
